// src/pages/SignInPage.js
import React, { useState, useEffect } from "react"; // 👈 Add useEffect
import { useNavigate, useLocation } from "react-router-dom";
import { useProfile } from "../context/ProfileContext"; // 👈 Add this import
import { getAuth, EmailAuthProvider, linkWithCredential } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
// MODIFIED: Import our service functions instead of raw Firebase methods
import { logIn, signUp, googleSignIn, createOrUpdateUserProfile, createNewUserProfileObject } from "../services/auth";
import ForgotPasswordModal from '../components/signIn/ForgotPasswordModal'; // Import the modal

import logo from "../assets/sign_up_logo.png";
import logoDark from "../assets/sign_up_logo_darkmode.png";
import toast from 'react-hot-toast';

const SignInPage = () => {
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showSignUpPopup, setShowSignUpPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false); // Optional: for disabling buttons on submit
  const [error, setError] = useState(""); // Optional: for displaying errors within the form
  const navigate = useNavigate(); // 👈 Add this
  const { user, userProfile, isDarkMode } = useProfile();
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setError("");
  };

  const isInAppBrowser = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      // Checks for Facebook, Instagram, and general WebView indicators
      return (
          /FBAN|FBAV/i.test(userAgent) ||
          /Instagram/i.test(userAgent) ||
          /WebView/i.test(userAgent) ||
          /CriOS/i.test(userAgent) // Chrome on iOS often uses a WebView
      );
  };

  const handleShowLogin = () => {
    resetForm();
    setShowSignUpPopup(false);
    setShowLoginPopup(true);
  };

  const handleShowSignUp = () => {
    resetForm();
    setShowLoginPopup(false);
    setShowSignUpPopup(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault(); // Prevent form submission refresh
    setLoading(true);
    setError("");
    try {
      await logIn(email, password);
      // No need to do anything else here.
      // The onAuthStateChanged listener in App.js will detect the new user
      // and handle navigation to the homepage.
      setShowLoginPopup(false); // Close the popup on success
    } catch (error) {
      console.error("Error logging in:", error);
      setError("Failed to login. Please check your email and password.");
    }
    setLoading(false);
  };

  const handleSignUp = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError("");

      const auth = getAuth();
      const currentUser = auth.currentUser;

      // Check if the current user is a guest
      if (currentUser && currentUser.isAnonymous) {
          try {
              const credential = EmailAuthProvider.credential(email, password);
              const linkResult = await linkWithCredential(currentUser, credential);
              const user = linkResult.user;
              
              // This uses the "golden template" to create a consistent profile
              const newUserProfile = createNewUserProfileObject(user);
              await createOrUpdateUserProfile(user.uid, newUserProfile);
              
              // ✅ Call the increment function for upgraded guest users
              const functions = getFunctions();
              const incrementUserCount = httpsCallable(functions, 'incrementUserCount');
              await incrementUserCount();

              setShowSignUpPopup(false);
              navigate("/onboarding");
          } catch (error) {
              console.error("Error upgrading anonymous account:", error);
              if (error.code === 'auth/email-already-in-use') {
                  setError("This email address is already in use by another account.");
              } else {
                  setError("Failed to upgrade account. Please try again.");
              }
          }
      } else {
          // This is the original flow for a brand new user
          try {
              // This now calls the updated signUp service, which handles the increment itself
              await signUp(email, password);
              setShowSignUpPopup(false);
              navigate("/onboarding");
          } catch (error) {
              console.error("Error signing up:", error);
              if (error.code === 'auth/email-already-in-use') {
                  setError("This email address is already in use.");
              } else {
                  setError("Failed to create account. Please try again.");
              }
          }
      }
      setLoading(false);
  };
  
  const handleGoogleSignIn = () => {
      // 1. Check if the user is in an in-app browser
      if (isInAppBrowser()) {
          // 2. If they are, show a helpful message instead of trying to log in.
          toast.error("Google Sign-In is not supported here. Please open this page in your phone's main browser (Chrome or Safari).", {
              duration: 6000, // Make the toast last longer
          });
          return; // Stop the function here
      }

      // 3. If they are in a normal browser, proceed with the login as usual.
      googleSignIn().catch(error => {
          console.error("Error initiating Google sign-in:", error);
          setError("Could not start Google Sign-In. Please try again.");
      });
  };

  const location = useLocation();

  useEffect(() => {
    if (location.state?.initialPopup === "login") {
      setShowLoginPopup(true);
      setShowSignUpPopup(false);
    } else if (location.state?.initialPopup === "signup") {
      setShowSignUpPopup(true);
      setShowLoginPopup(false);
    }
  }, [location.state]);

  useEffect(() => {
      // 1. If the top-level profile context is actively processing a redirect handshake, 
      // stop here and wait for it to finish updating the global state.
      if (loading) {
          
          return;
      }

      if (user) {
          
      }
      

      // 2. Check if a non-anonymous user has completed authentication
      if (user && !user.isAnonymous) {
          
          
          if (userProfile) {
              
              if (userProfile.isOnboarded === true) {
                  
                  navigate("/home");
              } else {
                  
                  navigate("/onboarding");
              }
              return; 
          }

          // 3. Fallback tracking protection window if Firestore document resolution is lagging
          if (!userProfile) {
              
              const fallbackTimeout = setTimeout(() => {
                  console.warn("TIMEOUT TRIGGERED: Routing to onboarding safety container.");
                  navigate("/onboarding");
              }, 1500);
              
              return () => clearTimeout(fallbackTimeout);
          }
      } else if (user && user.isAnonymous) {
          
      } else {
          
      }
      
  }, [user, userProfile, loading, navigate]); 

  // Dynamic Theme Classes
  const bgClass = isDarkMode ? "bg-brand-black" : "bg-gray-100";
  const cardClass = isDarkMode ? "bg-zinc-900 border border-zinc-800" : "bg-white";
  const textPrimary = isDarkMode ? "text-white" : "text-slate-900";
  const inputClass = isDarkMode 
    ? "bg-zinc-800 border-zinc-700 text-white focus:border-brand-pink" 
    : "bg-white border-gray-300 text-black focus:border-blue-500";

  return (
    <div className={`h-screen w-full flex flex-col items-center py-4 px-6 justify-between overflow-hidden transition-colors duration-500 ${bgClass}`}>
      <div></div>

      <div className="w-full flex flex-col items-center">
        {/* a) Logo */}
        <img 
          src={isDarkMode ? logoDark : logo}
          alt="Logo" 
          className={`w-48 h-auto object-contain mb-12 transition-all duration-700 ${
            isDarkMode ? "brightness-110 drop-shadow-[0_0_15px_rgba(255,0,204,0.3)]" : ""
          }`}
        />

        {/* b) Login and Sign Up Buttons */}
        <div className="flex flex-col space-y-5 w-full max-w-xs items-center">
          <button
            onClick={handleShowLogin}
            className={`w-[70%] px-6 py-4 font-black uppercase tracking-widest rounded-xl shadow-xl transition-all active:scale-95 ${
              isDarkMode ? "bg-brand-pink text-white" : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Login
          </button>
          <button
            onClick={handleShowSignUp}
            className={`w-[70%] px-6 py-4 font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 border-2 ${
              isDarkMode 
                ? "border-brand-pink text-brand-pink bg-transparent hover:bg-brand-pink hover:text-white" 
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            Sign Up
          </button>
        </div>
      </div>

      {/* d) Terms and Conditions */}
      <div className="text-center pb-8">
        <p className={`text-[10px] uppercase font-bold tracking-widest transition-colors ${isDarkMode ? "text-zinc-500" : "text-gray-400"}`}>
          By entering, you agree to our 
          <a href="/terms" className={`ml-1 underline ${isDarkMode ? "text-brand-pink" : "hover:text-gray-700"}`}>Terms</a>.
        </p>
      </div>

      {/* --- POPUPS (Login & Sign Up) --- */}
      {(showLoginPopup || showSignUpPopup) && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-50 p-4">
          <form 
            onSubmit={showLoginPopup ? handleLogin : handleSignUp} 
            className={`p-8 rounded-2xl shadow-2xl w-full max-w-sm transition-all duration-300 ${cardClass}`}
          >
            <h2 className={`text-2xl font-black italic tracking-tighter mb-6 text-center uppercase ${textPrimary}`}>
              {showLoginPopup }
            </h2>
            
            {error && <p className="text-red-500 text-[10px] font-bold uppercase mb-4 text-center tracking-tighter">{error}</p>}
            
            <div className="space-y-4">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full p-4 rounded-xl outline-none border transition-all ${inputClass}`}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full p-4 rounded-xl outline-none border transition-all ${inputClass}`}
                  required
                />
            </div>

            {showLoginPopup && (
              <div className="text-center my-4"> 
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordOpen(true)}
                  className={`text-[12px] font-black uppercase tracking-widest ${isDarkMode ? "text-zinc-400 hover:text-brand-pink" : "text-blue-600"}`}
                >
                  Forgot Password?
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 mt-2 font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-95 disabled:opacity-50 ${
                isDarkMode ? "bg-brand-pink text-white" : "bg-blue-600 text-white"
              }`}
            >
              {loading ? (showLoginPopup ? "Authenticating..." : "Creating...") : (showLoginPopup ? "Login" : "Create Account")}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className={`w-full border-t ${isDarkMode ? "border-zinc-800" : "border-gray-200"}`}></div></div>
              <div className="relative flex justify-center text-[12px] font-black"><span className={`px-4 uppercase ${isDarkMode ? "bg-zinc-900 text-zinc-600" : "bg-white text-gray-400"}`}>OR</span></div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className={`w-full py-3 flex items-center justify-center gap-3 font-bold text-xs border rounded-xl transition-all ${
                isDarkMode ? "border-zinc-700 text-white hover:bg-zinc-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Continue with Google
            </button>

            <button
              type="button"
              onClick={() => { setShowLoginPopup(false); setShowSignUpPopup(false); }}
              className={`w-full py-2 mt-4 text-[12px] font-black uppercase tracking-widest ${isDarkMode ? "text-zinc-400 hover:text-white" : "text-gray-400"}`}
            >
              Go Back
            </button>
          </form>
          
          <ForgotPasswordModal
            isOpen={isForgotPasswordOpen}
            onClose={() => setIsForgotPasswordOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

export default SignInPage;